import type { Messages } from '../en'

export const access: Messages['access'] = {
  title: 'Acessos',
  subtitle: (tenant) => `Quem pode usar o portal de ${tenant} e como entra.`,
  stepUp:
    'Você entrou com um código por e-mail. Gerenciar acessos exige uma chave de acesso: saia e entre novamente — logo após o código, escolha "Criar chave de acesso" (ou use a que você já tem).',

  policyTitle: 'Política de acesso',
  policyPasskey: 'Chave de acesso (ou SSO)',
  policyEmailCode: 'Chave de acesso, SSO ou código por e-mail',
  policyDomainsAny: 'Convites para qualquer domínio de e-mail',
  policyDomains: (domains) => `Convites somente para ${domains}`,
  policyManagedByEvt: 'Definida pela EVT. Fale com seu contato na EVT para alterá-la.',

  inviteTitle: 'Convidar pessoas',
  inviteHelp:
    'Um endereço de e-mail por linha (ou separados por vírgula). Cada pessoa recebe um e-mail de convite com os passos para entrar.',
  inviteEmails: 'Endereços de e-mail',
  inviteEmailsPlaceholder: 'ana@empresa.com.br\nbruno@empresa.com.br',
  inviteRole: 'Papel',
  inviteSendEmail: 'Enviar o e-mail de convite',
  inviteLanguage: 'Idioma do e-mail',
  inviteSubmit: 'Convidar',
  inviteOutcomeInvited: 'Convidado(a)',
  inviteOutcomeReactivated: 'Acesso restaurado',
  inviteOutcomeAlready: 'Já tem acesso',
  inviteEmailFailed: 'falha ao enviar o e-mail — use "Reenviar convite"',
  inviteEmailed: 'e-mail enviado',
  inviteOutside: (emails, domains) =>
    emails.length === 1
      ? `Nenhum convite enviado: ${emails[0]} está fora de ${domains}. Corrija ou remova esse endereço e convide de novo.`
      : `Nenhum convite enviado: ${emails.join(', ')} estão fora de ${domains}. Corrija ou remova esses endereços e convide de novo.`,

  roleMember: 'Membro',
  roleAdmin: 'Administrador(a)',
  roleHelp: 'Administradores também gerenciam quem tem acesso.',

  membersTitle: 'Pessoas',
  membersEmpty: 'Ninguém tem acesso ainda. Convide as primeiras pessoas acima.',
  searchPlaceholder: 'Buscar por e-mail',
  showRevoked: 'Mostrar pessoas removidas',
  colPerson: 'Pessoa',
  colRole: 'Papel',
  colStatus: 'Situação',
  colSignIn: 'Acesso',
  colActions: '',
  statusActive: 'Ativo',
  statusInvited: 'Convidado',
  statusRevoked: 'Removido',
  neverSignedIn: 'Nunca entrou',
  lastSignIn: (ago) => `Último acesso ${ago}`,
  passkeys: (n) => (n === 1 ? '1 chave de acesso' : `${n} chaves de acesso`),
  sso: 'SSO',
  you: 'você',
  viaDomain: 'pelo domínio de e-mail',
  viaSso: 'pelo SSO da empresa',
  otherTenants: (n) => (n === 1 ? 'também em 1 outra empresa' : `também em ${n} outras empresas`),

  actionMakeAdmin: 'Tornar administrador(a)',
  actionMakeMember: 'Tornar membro',
  actionRevoke: 'Remover acesso',
  actionReactivate: 'Restaurar acesso',
  actionResend: 'Reenviar convite',
  actionSignOut: 'Encerrar sessões',
  actionResetPasskeys: 'Redefinir chaves de acesso',
  confirm: 'Confirmar',
  confirmRevoke: (email) => `Remover o acesso de ${email}? A pessoa é desconectada na hora.`,
  confirmResetPasskeys: (email) =>
    `Apagar todas as chaves de acesso de ${email}? A pessoa entra de novo com um código por e-mail e cria uma nova.`,
  confirmSignOut: (email) => `Encerrar as sessões de ${email} em todos os dispositivos?`,
  doneResent: 'Convite reenviado.',
  doneSignedOut: 'Sessões encerradas.',
  donePasskeysReset: (n) => (n === 1 ? '1 chave de acesso apagada.' : `${n} chaves de acesso apagadas.`),

  activityTitle: 'Alterações recentes',
  activityEmpty: 'Nenhuma alteração ainda.',
  activityBy: (actor, platform) => (platform ? `${actor} (EVT)` : actor),
  actions: {
    'tenant.created': 'criou a empresa',
    'tenant.updated': 'alterou as configurações da empresa',
    'tenant.flows_changed': 'alterou os fluxos da empresa',
    'member.invited': 'convidou',
    'member.reinvited': 'reenviou o convite para',
    'member.role_changed': 'alterou o papel de',
    'member.revoked': 'removeu o acesso de',
    'member.reactivated': 'restaurou o acesso de',
    'member.signed_out': 'encerrou as sessões de',
    'member.passkeys_reset': 'redefiniu as chaves de acesso de'
  },
  activityFlowsAdded: (names) => `adicionou ${names}`,
  activityFlowsRemoved: (names) => `removeu ${names}`,
  activityFlowsModeAll: 'agora todos os fluxos oferecidos',
  activityFlowsModeSelected: 'agora só os fluxos escolhidos',

  tenantsTitle: 'Empresas',
  tenantsSubtitle:
    'Todas as empresas do portal. Crie uma empresa, nomeie o primeiro administrador e defina como as pessoas entram.',
  createTitle: 'Nova empresa',
  createHelp:
    'O identificador deve ser exatamente o contractor id usado no fr-docs (linhas de fluxo, segredo de assinatura) e no mail-reader — o mesmo texto em todos.',
  fieldId: 'Contractor id',
  fieldIdPlaceholder: 'heineken-br',
  fieldName: 'Nome de exibição',
  fieldNamePlaceholder: 'Heineken Brasil',
  fieldFirstAdmin: 'Primeiro administrador (e-mail)',
  fieldEmailCode: 'Permitir entrar com código por e-mail',
  fieldEmailCodeHelp:
    'Para empresas cujos computadores gerenciados não comportam chave de acesso. O código prova apenas o controle da caixa de e-mail; gerenciar acessos continua exigindo chave de acesso ou SSO.',
  fieldDomains: 'Domínios de e-mail permitidos nos convites',
  fieldDomainsHelp: 'Separados por vírgula, ex.: heineken.com.br. Vazio = qualquer domínio.',
  createFlowsHelp:
    'Uma empresa nova não vê nenhum fluxo até você escolher os fluxos dela (uma que já tem pessoas mantém os delas até você escolher). A página da empresa abre assim que ela é criada: escolha lá.',
  createSubmit: 'Criar empresa',
  created: (name) => `${name} criada.`,
  colTenant: 'Empresa',
  colMembers: 'Pessoas',
  colPolicy: 'Acesso',
  colFlows: 'Fluxos',
  flowsAllShort: 'Todos os oferecidos',
  flowsAllShortUnchosen: 'Todos os oferecidos · ainda não escolhidos',
  flowsChosenShort: (n) => (n === 0 ? 'Nenhum escolhido' : n === 1 ? '1 fluxo' : `${n} fluxos`),
  countsLine: (active, admins, invited) =>
    `${active} ${active === 1 ? 'pessoa' : 'pessoas'} · ${admins} admin${admins === 1 ? '' : 's'}${invited ? ` · ${invited} ainda não ${invited === 1 ? 'entrou' : 'entraram'}` : ''}`,
  notRegistered: 'ainda não configurada',
  statusSuspended: 'Suspensa',
  manage: 'Gerenciar',
  tenantsEmpty: 'Nenhuma empresa ainda.',

  settingsTitle: 'Configurações da empresa',
  saveSettings: 'Salvar configurações',
  saved: 'Configurações salvas.',
  suspend: 'Suspender empresa',
  activate: 'Reativar empresa',
  confirmSuspend: (name) => `Suspender ${name}? Todos são desconectados e ninguém entra até que você a reative.`,

  readinessTitle: 'Prontidão',
  readinessHelp: 'O que esta empresa ainda precisa para que as pessoas executem fluxos de ponta a ponta.',
  checks: {
    registered: 'Empresa configurada no portal',
    active: 'Empresa ativa',
    signing_secret: 'O portal tem o segredo de assinatura da empresa (o contractor secret do fr-docs)',
    signing_accepted: 'O fr-docs aceita a assinatura do portal para esta empresa',
    flows_chosen: 'Os fluxos da empresa foram escolhidos pela EVT',
    flows_visible: 'Pelo menos um fluxo visível para a empresa',
    has_admin: 'A empresa tem um administrador'
  },
  checkFix: {
    registered: 'Salve as configurações acima: isso registra a empresa.',
    active: 'Reative a empresa acima.',
    signing_secret:
      'Registre o contractor secret no smith-factory-web → Contractor secrets e entregue o mesmo valor ao portal (o CONTRACTOR_WEBHOOK_SECRETS_JSON dele, ou o Key Vault quando o portal o ler de lá) e reinicie o portal. O mail-reader-web precisa do mesmo valor para pedidos por e-mail.',
    signing_accepted:
      'O portal e o fr-docs têm segredos diferentes para este id (ou o fr-docs não tem nenhum). Use o mesmo valor nos dois lados.',
    flows_chosen:
      'Ninguém escolheu ainda, então ela vê todos os fluxos oferecidos — inclusive os publicados depois. Escolha em Fluxos, acima (manter todos também é uma escolha: salve assim).',
    flows_visible:
      'Escolha pelo menos um fluxo em Fluxos, acima. Se nenhum for oferecido, publique um para esta empresa antes: smith-factory-web → o fluxo → Customer portal → visible.',
    has_admin: 'Convide um administrador abaixo.'
  },
  signingUnreachable: (detail) => `Não foi possível consultar o fr-docs: ${detail}`,
  recheck: 'Verificar de novo',
  flowsVisible: (names) => `Fluxos visíveis: ${names}`,
  flowsUnknown: 'Não foi possível consultar o fr-docs sobre os fluxos visíveis.',
  backToTenants: '← Todas as empresas',

  flowsTitle: 'Fluxos',
  flowsHelp:
    'Quais fluxos as pessoas desta empresa veem, executam e com quais guardam memórias. Só aparecem os fluxos que o fr-docs oferece a esta empresa: os que ela pode executar e que estão publicados no portal do cliente (smith-factory-web → o fluxo → Customer portal).',
  flowsModeSelected: 'Somente os fluxos escolhidos abaixo',
  flowsModeSelectedHelp: 'Um fluxo publicado depois fica de fora até você escolhê-lo.',
  flowsModeAll: 'Todos os fluxos oferecidos a esta empresa',
  flowsModeAllHelp:
    'Inclusive os publicados depois. Pensado para a própria EVT; empresas criadas antes desta escolha existir ficam aqui até você escolher.',
  flowsNoneOffered:
    'O fr-docs ainda não oferece nenhum fluxo a esta empresa. Publique um para ela antes: smith-factory-web → o fluxo → Customer portal → visible.',
  flowsOfferedError: (detail) =>
    `Não foi possível consultar o fr-docs sobre os fluxos oferecidos a esta empresa (${detail}). Só os fluxos escolhidos aparecem: você pode removê-los, não adicionar outros.`,
  flowsNotOffered: 'O fr-docs não o oferece mais a esta empresa — fica oculto qualquer que seja a escolha. Remova-o.',
  flowsNotPublished: 'Não publicado no portal: aparece só porque este ambiente mostra todos os fluxos.',
  flowsSummary: (n) =>
    n === 0
      ? 'Nada escolhido: as pessoas não veem nenhum fluxo.'
      : n === 1
        ? '1 fluxo escolhido.'
        : `${n} fluxos escolhidos.`,
  flowsSummaryAll: (n) =>
    n === 1 ? 'As pessoas veem o 1 fluxo oferecido hoje.' : `As pessoas veem os ${n} fluxos oferecidos hoje.`,
  flowsSave: 'Salvar fluxos',
  flowsSaved: 'Fluxos salvos. Valem a partir da próxima ação de cada pessoa — ninguém é desconectado.',
  flowsReload: 'Recarregar',
  flowsUnsaved: 'Alterações não salvas.',
  flowsNotChosen:
    'Ninguém escolheu ainda os fluxos desta empresa: as pessoas veem todos os fluxos oferecidos a ela, inclusive os publicados depois. Escolha abaixo e salve — ou salve como está para manter todos.'
}
